import React, { useState, createContext } from 'react'
import CustomAgentService from '../../services/CustomAgent/CustomAgent.service'
import PropTypes from 'prop-types'

export const CustomAgentContext = createContext()

export const CustomAgentContextProvider = (props) => {
  const [agents, setAgents] = useState([])
  const [currentAgent, setCurrentAgent] = useState(null)
  const [conversations, setConversations] = useState([])
  const [currentConversation, setCurrentConversation] = useState(null)
  const [loading, setLoading] = useState(false)

  const fetchAgents = async (portalName) => {
    try {
      setLoading(true)
      const res = await CustomAgentService.getAgents(portalName)
      setAgents(res.data)
    } catch (error) {
      console.error('Error fetching agents:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchAgent = async (agentId) => {
    try {
      setLoading(true)
      const res = await CustomAgentService.getAgent(agentId)
      setCurrentAgent(res.data)
      return res.data
    } catch (error) {
      console.error('Error fetching agent:', error)
    } finally {
      setLoading(false)
    }
  }

  const createAgent = async (data) => {
    const res = await CustomAgentService.createAgent(data)
    setAgents(prev => [res.data, ...prev])
    return res.data
  }

  const updateAgent = async (agentId, data) => {
    const res = await CustomAgentService.updateAgent(agentId, data)
    setAgents(prev => prev.map(a => a.id === agentId ? { ...a, ...res.data } : a))
    if (currentAgent?.id === agentId) {
      setCurrentAgent(prev => ({ ...prev, ...res.data }))
    }
    return res.data
  }

  const deleteAgent = async (agentId) => {
    await CustomAgentService.deleteAgent(agentId)
    setAgents(prev => prev.filter(a => a.id !== agentId))
    if (currentAgent?.id === agentId) {
      setCurrentAgent(null)
    }
  }

  const uploadDocuments = async (agentId, files) => {
    const res = await CustomAgentService.uploadDocuments(agentId, files)
    // Refresh agent to get updated documents list
    await fetchAgent(agentId)
    return res.data
  }

  const deleteDocument = async (agentId, documentId) => {
    await CustomAgentService.deleteDocument(agentId, documentId)
    if (currentAgent?.id === agentId) {
      setCurrentAgent(prev => ({
        ...prev,
        documents: prev.documents.filter(d => d.id !== documentId)
      }))
    }
  }

  const fetchConversations = async (agentId) => {
    try {
      const res = await CustomAgentService.getConversations(agentId)
      setConversations(res.data)
    } catch (error) {
      console.error('Error fetching conversations:', error)
    }
  }

  const fetchConversation = async (agentId, conversationId) => {
    try {
      const res = await CustomAgentService.getConversation(agentId, conversationId)
      setCurrentConversation(res.data)
      return res.data
    } catch (error) {
      console.error('Error fetching conversation:', error)
    }
  }

  const sendMessage = async (agentId, message, conversationId) => {
    const res = await CustomAgentService.sendMessage(agentId, message, conversationId)
    return res.data
  }

  const sendMessageStream = async (agentId, message, conversationId, { onToken, onStart }) => {
    return CustomAgentService.sendMessageStream(agentId, message, conversationId, { onToken, onStart })
  }

  const deleteConversation = async (agentId, conversationId) => {
    await CustomAgentService.deleteConversation(agentId, conversationId)
    setConversations(prev => prev.filter(c => c.id !== conversationId))
    if (currentConversation?.id === conversationId) {
      setCurrentConversation(null)
    }
  }

  return (
    <CustomAgentContext.Provider
      value={{
        agents,
        currentAgent,
        conversations,
        currentConversation,
        loading,
        fetchAgents,
        fetchAgent,
        createAgent,
        updateAgent,
        deleteAgent,
        uploadDocuments,
        deleteDocument,
        fetchConversations,
        fetchConversation,
        sendMessage,
        sendMessageStream,
        deleteConversation,
        setCurrentAgent,
        setCurrentConversation
      }}
    >
      {props.children}
    </CustomAgentContext.Provider>
  )
}

CustomAgentContextProvider.propTypes = {
  children: PropTypes.node
}
